import { db } from '@/firebase'
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  type DocumentData
} from 'firebase/firestore'

export interface BankAccount {
  bank: string
  agency: string
  account: string
  type: 'checking' | 'savings'
  pixKey?: string
}

export interface CostCenter {
  costCenterId: string
  costTypeId: string
  costCenterName: string
  costTypeName: string
}

export interface Address {
  cep: string
  street: string
  number: string
  complement?: string
  neighborhood: string
  city: string
  state: string
}

export interface Supplier {
  id: string
  name: string
  cnpj: string
  email: string
  phone: string
  address: Address
  active: boolean
  organizationId: string
  createdAt: Date
  updatedAt: Date
  bankAccounts: BankAccount[]
  costCenters: CostCenter[]
}

const COLLECTION_NAME = 'suppliers'

export const getSuppliers = async (organizationId: string): Promise<Supplier[]> => {
  try {
    const suppliersRef = collection(db, COLLECTION_NAME)
    const q = query(suppliersRef, where('organizationId', '==', organizationId))
    const querySnapshot = await getDocs(q)
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate()
    })) as Supplier[]
  } catch (error) {
    console.error('Error getting suppliers:', error)
    throw error
  }
}

export const createSupplier = async (
  organizationId: string,
  supplier: Omit<Supplier, 'id' | 'active' | 'createdAt' | 'updatedAt' | 'organizationId'>
): Promise<Supplier> => {
  try {
    const suppliersRef = collection(db, COLLECTION_NAME)
    const newSupplier = {
      ...supplier,
      organizationId,
      active: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }
    
    const docRef = await addDoc(suppliersRef, newSupplier)
    return {
      id: docRef.id,
      ...newSupplier,
      createdAt: new Date(),
      updatedAt: new Date()
    } as Supplier
  } catch (error) {
    console.error('Error creating supplier:', error)
    throw error
  }
}

export const updateSupplier = async (
  id: string,
  data: Partial<Supplier>
): Promise<Partial<Supplier>> => {
  try {
    const supplierRef = doc(db, COLLECTION_NAME, id)
    const updateData = {
      ...data,
      updatedAt: serverTimestamp()
    }
    
    await updateDoc(supplierRef, updateData)
    return {
      id,
      ...updateData,
      updatedAt: new Date()
    }
  } catch (error) {
    console.error('Error updating supplier:', error)
    throw error
  }
}

export const deleteSupplier = async (id: string): Promise<void> => {
  try {
    const supplierRef = doc(db, COLLECTION_NAME, id)
    await deleteDoc(supplierRef)
  } catch (error) {
    console.error('Error deleting supplier:', error)
    throw error
  }
}